import React, { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
  Container,
} from "react-bootstrap";
import { FaPlus, FaDesktop } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import infoIcon from "../pages/images/info.svg";
import { api_url } from "../config.js";

import { desktopCreate } from "../slices/desktopSlice";
import {
  listTemplates,
  listFlavors,
  listOSs,
  templateCreate,
} from "../slices/templateSlice";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ModalCreateTemplates from "../components/ModalCreateTemplates";
import "./Common.css";

const CreateDesktopPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [formDataDesktops, setFormDataDesktops] = useState({
    name: "",
    description: "",
    templateId: "",
    flavor: "",
    disk: "",
    backing_file: "",
    os: "",
  });
  const [desktopsModalErrors, setDesktopsModalErrors] = useState({});
  const [showModalCreateTemplates, setShowModalCreateTemplates] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formDataTemplates, setFormDataTemplates] = useState({
    name: "",
    description: "",
    flavor: "",
    disk: "",
    backing_file: "",
  });
  const [templateModalErrors, setTemplateModalErrors] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const { userInfo } = useSelector((state) => state.user);
  const {
    templates: templatesList,
    flavors: flavorsList,
    oss: osList,
  } = useSelector((state) => state.template);

  useEffect(() => {
    if (!templatesList?.length) dispatch(listTemplates());
    if (!flavorsList?.length) dispatch(listFlavors());
    if (!osList?.length) dispatch(listOSs());
  }, [dispatch, templatesList?.length, flavorsList?.length, osList?.length]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const templateIdFromUrl = searchParams.get("templateId");
    const nameFromUrl = searchParams.get("name");

    setFormDataDesktops((prev) => {
      // skip update if both template ID and name perfectly match the current state
      if (
        String(prev.templateId) === String(templateIdFromUrl || "") &&
        (nameFromUrl === null || prev.name === nameFromUrl)
      ) {
        return prev;
      }

      let updatedForm = { ...prev };

      // apply the name from the URL if there is one
      if (nameFromUrl !== null) {
        updatedForm.name = nameFromUrl;
      }

      if (templateIdFromUrl && templatesList?.length > 0) {
        const matchingTemplate = templatesList.find(
          (t) => String(t.id) === String(templateIdFromUrl),
        );
        if (matchingTemplate) {
          updatedForm = {
            ...updatedForm,
            flavor:
              matchingTemplate.flavor ||
              matchingTemplate.flavorName ||
              matchingTemplate.flavorId ||
              "",
            os: matchingTemplate.os,
            disk: matchingTemplate.disk,
            backing_file: matchingTemplate.backing_file,
            templateId: matchingTemplate.id,
          };

          // auto-fill template name only if the URL didn't provide a name and the field is empty
          if (!updatedForm.name) {
            updatedForm.name = matchingTemplate.name;
          }
        } else {
          // if the URL has an invalid ID
          updatedForm.templateId = templateIdFromUrl;
        }
      } else if (!templateIdFromUrl) {
        // if the URL has no ID, make sure the template fields are empty
        updatedForm.templateId = "";
        updatedForm.flavor = "";
        updatedForm.os = "";
        updatedForm.disk = "";
        updatedForm.backing_file = "";
      }

      return updatedForm;
    });
  }, [location.search, templatesList]);

  const selectedTemplate = useMemo(() => {
    if (!formDataDesktops?.templateId || !templatesList) return null;
    return templatesList.find(
      (t) => String(t.id) === String(formDataDesktops.templateId),
    );
  }, [formDataDesktops?.templateId, templatesList]);

  const selectedFlavor = useMemo(() => {
    if (!selectedTemplate || !flavorsList) return null;
    const templateFlavorStr = String(
      selectedTemplate.flavorName || selectedTemplate.flavor || "",
    )
      .trim()
      .toLowerCase();
    const templateFlavorId = String(
      selectedTemplate.flavorId ||
        selectedTemplate.flavorName ||
        selectedTemplate.flavor ||
        "",
    ).trim();

    return flavorsList.find(
      (f) =>
        String(f.name).trim().toLowerCase() === templateFlavorStr ||
        String(f.id) === templateFlavorId,
    );
  }, [selectedTemplate, flavorsList]);

  const handleInputChangeDesktops = (e) => {
    const { name, value } = e.target;

    setFormDataDesktops((prev) => {
      const updatedForm = { ...prev, [name]: value };

      // --- Elephant Easter Egg 🐘 ---
      // Type "Elephant" or "Elefant" in the "Desktop Name" field to check the Easter egg :)
      if (name === "name") {
        const desktopNameLower = value.trim().toLowerCase();

        if (desktopNameLower === "elefant" || desktopNameLower === "elephant") {
          const currentDesc = updatedForm.description || "";

          // Only append the emoji if it's not already there to prevent duplicates
          if (!currentDesc.includes("🐘")) {
            updatedForm.description = currentDesc ? currentDesc + " 🐘" : "🐘";
          }
        }
      }

      return updatedForm;
    });

    // instantly sync desktop name changes in the URL
    if (name === "name") {
      const searchParams = new URLSearchParams(location.search);
      if (value) searchParams.set("name", value);
      else searchParams.delete("name");
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  };

  const handleInputChangeTemplates = (e) => {
    const { name, value } = e.target;
    setFormDataTemplates((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTemplate = (e) => {
    e?.preventDefault();
    const errors = {};
    if (!formDataTemplates.name) errors.name = "Template Name is required";
    if (Object.keys(errors).length > 0) {
      setTemplateModalErrors(errors);
      return;
    }
    setLoadingTemplates(true);
    dispatch(templateCreate(formDataTemplates))
      .unwrap()
      .then(() => {
        toast.success("Template created successfully.");
        setShowModalCreateTemplates(false);
        setFormDataTemplates({
          name: "",
          description: "",
          flavor: "",
          disk: "",
          backing_file: "",
        });
        setTemplateModalErrors({});
        dispatch(listTemplates());
      })
      .catch((err) => {
        toast.error(
          err.detail ||
            (typeof err === "string" ? err : "Failed to create template."),
        );
      })
      .finally(() => {
        setLoadingTemplates(false);
      });
  };

  const getNewDesktopName = ({ prevState, template, templatesList }) => {
    const currentName = prevState.name || "";
    const isNameEmpty = currentName.trim() === "";

    let wasAutoFilled = false;

    if (prevState.templateId && templatesList?.length > 0) {
      const prevTemplate = templatesList.find(
        (t) => String(t.id) === String(prevState.templateId),
      );

      if (prevTemplate && currentName === prevTemplate.name) {
        wasAutoFilled = true;
      }
    }

    // overwrite the desktop name if the field is empty or was previously auto-filled
    if (isNameEmpty || wasAutoFilled) {
      return template ? template.name : "";
    }

    return currentName;
  };

  const handleTemplateSelect = (template) => {
    const newDesktopName = getNewDesktopName({
      prevState: formDataDesktops,
      template,
      templatesList,
    });

    setFormDataDesktops((prevState) => ({
      ...prevState,
      flavor:
        template?.flavor || template?.flavorName || template?.flavorId || "",
      disk: template?.disk || "",
      templateId: template?.id || "",
      backing_file: template?.backing_file || "",
      os: template?.os || "",
      name: newDesktopName,
    }));

    // update URL with template ID and desktop name
    const searchParams = new URLSearchParams(location.search);

    if (template?.id) searchParams.set("templateId", template.id);
    else searchParams.delete("templateId");

    if (newDesktopName) searchParams.set("name", newDesktopName);
    else searchParams.delete("name");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const onTemplateChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      handleTemplateSelect(null);
      return;
    }
    const template = templatesList.find(
      (t) => String(t.id) === String(selectedId),
    );
    if (template) handleTemplateSelect(template);
  };

  const handleCreateDesktop = (e) => {
    e?.preventDefault();
    const errors = {};
    if (!formDataDesktops.name) errors.name = "Desktop Name is required";
    if (!formDataDesktops.templateId)
      errors.templateId = "Template is required";

    if (Object.keys(errors).length > 0) {
      setDesktopsModalErrors(errors);
      return;
    }

    setIsSubmitting(true);

    const backendData = {
      Name: formDataDesktops.name,
      Description: formDataDesktops.description,
      TemplateId: formDataDesktops.templateId,
      FlavorName: formDataDesktops.flavor,
      ProviderName: formDataDesktops.providerName || "",
      PendingAction: "Start", // force initial power on
      DeleteAfterInactivitySeconds:
        formDataDesktops.deleteAfterInactivitySeconds || 0,
      RecycleAfterUseSeconds: formDataDesktops.recycleAfterUseSeconds || 0,
    };

    dispatch(desktopCreate(backendData))
      .unwrap()
      .then(() => {
        toast.success("Desktop creation initiated successfully.");
        navigate("/"); // send user back to homepage
      })
      .catch((err) => {
        toast.error(
          err.detail || (typeof err === "string" ? err : "Validation failed."),
        );
        setIsSubmitting(false);
      });
  };

  const formatValue = (value) => {
    if (Array.isArray(value))
      return value.map((item) => formatValue(item)).join(", ");
    if (typeof value === "object" && value !== null) {
      if ("key" in value && "value" in value)
        return `${value.key}: ${formatValue(value.value)}`;
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${formatValue(v)}`)
        .join(", ");
    }
    return String(value ?? "-");
  };

  // Validation
  const missingTemplate = !selectedTemplate;
  const missingName = !formDataDesktops.name;

  // Create button disables if data is missing OR if the backend is currently processing
  const hasValidationError = missingTemplate || missingName;
  const isDisabled = hasValidationError || isSubmitting;

  const tooltipMessage =
    missingTemplate && missingName
      ? "Select a template and enter the desktop name"
      : missingTemplate
        ? "Select a template"
        : missingName
          ? "Enter the desktop name"
          : "";

  return (
    <div className="main-container">
      <Header />

      <Container
        className="px-3 py-4 flex-grow-1"
        style={{ maxWidth: "1100px" }}
      >
        <div className="page-header-flex mb-4" style={{ padding: "0" }}>
          <h2 style={{ fontSize: "27px", margin: 0, color: "#333" }}>
            Create Desktop
          </h2>
        </div>

        {!userInfo?.isAdmin && templatesList?.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h4 style={{ color: "#6c757d" }}>
              You can't create a desktop since there are no templates available.
            </h4>
          </div>
        ) : (
          <Row className="g-4 justify-content-center">
            {/* LEFT SECTION: Form */}
            <Col xs={12} md={5}>
              <Form onSubmit={(e) => e.preventDefault()}>
                {/* Templates Area */}
                <Form.Group
                  controlId="templateSelect"
                  style={{ marginBottom: "20px" }}
                >
                  <Form.Label
                    style={{ fontWeight: "bold", fontSize: "1.1rem" }}
                  >
                    Select Template
                  </Form.Label>
                  <div
                    style={{
                      border: "1px solid #ced4da",
                      borderRadius: "8px",
                      maxHeight: "310px",
                      overflowY: "auto",
                      backgroundColor: "#fff",
                      boxShadow: "inset 0 2px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    {templatesList?.map((template) => {
                      const isSelected =
                        String(formDataDesktops.templateId) ===
                        String(template.id);
                      const matchedOs = osList?.find(
                        (os) =>
                          String(os.id).toLowerCase() ===
                          String(template.operatingSystemId).toLowerCase(),
                      );

                      let iconSrc = FaDesktop;
                      if (matchedOs?.iconUri) {
                        const uri = matchedOs.iconUri;
                        if (uri.startsWith("http") || uri.startsWith("data:")) {
                          iconSrc = uri;
                        } else {
                          const cleanBase = api_url.replace(/\/$/, "");
                          const cleanPath = uri.startsWith("/")
                            ? uri
                            : `/${uri}`;
                          iconSrc = `${cleanBase}${cleanPath}`;
                        }
                      }

                      return (
                        <div
                          key={template.id}
                          onClick={() =>
                            onTemplateChange({ target: { value: template.id } })
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "14px 18px",
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? "#eef3fc"
                              : "transparent",
                            borderBottom: "1px solid #f0f0f0",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected)
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected)
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                          }}
                        >
                          {/* OS Icon */}
                          <img
                            src={iconSrc}
                            alt={matchedOs?.name || "OS Icon"}
                            style={{
                              width: "26px",
                              height: "26px",
                              minWidth: "26px",
                              marginRight: "14px",
                              objectFit: "contain",
                            }}
                            // If the URL is broken, safely fall back to default desktop icon
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = FaDesktop;
                            }}
                          />
                          <span
                            style={{
                              fontWeight: isSelected ? "600" : "500",
                              color: isSelected ? "#0056b3" : "#333",
                              fontSize: "1.05rem",
                            }}
                          >
                            {template.name}
                          </span>
                        </div>
                      );
                    })}

                    {/* Fallback if list happens to be empty */}
                    {templatesList?.length === 0 && (
                      <div
                        style={{
                          padding: "20px",
                          color: "#6c757d",
                          fontStyle: "italic",
                          textAlign: "center",
                        }}
                      >
                        No templates available
                      </div>
                    )}
                  </div>

                  {/* Invalid Template ID Warning */}
                  {templatesList?.length > 0 &&
                    formDataDesktops.templateId &&
                    !selectedTemplate && (
                      <div
                        style={{
                          fontSize: "0.9em",
                          color: "#842029",
                          backgroundColor: "#f8d7da",
                          border: "1px solid #f5c6cb",
                          padding: "10px",
                          marginTop: "12px",
                          borderRadius: "6px",
                        }}
                      >
                        There is no template with the provided ID. Please select
                        one from the list above.
                      </div>
                    )}
                </Form.Group>

                {/* Add a Template Button (Admins Only) */}
                {userInfo?.isAdmin && (
                  <div
                    onClick={() => setShowModalCreateTemplates(true)}
                    style={{
                      border: "1px dashed #080d8a",
                      padding: "12px",
                      marginBottom: "25px",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                      color: "#080d8a",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#eef3fc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                  >
                    <FaPlus size={14} />
                    <span style={{ fontWeight: "500" }}>Add New Template</span>
                  </div>
                )}

                {/* Desktop Name */}
                <Form.Group controlId="name" style={{ marginBottom: "20px" }}>
                  <Form.Label
                    style={{ fontWeight: "bold", fontSize: "1.1rem" }}
                  >
                    Desktop Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Add a desktop name"
                    name="name"
                    value={formDataDesktops.name ?? ""}
                    onChange={handleInputChangeDesktops}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                      }
                    }}
                    required
                    style={{ padding: "10px", borderRadius: "6px" }}
                  />
                  {desktopsModalErrors.name && (
                    <div
                      style={{
                        fontSize: "0.85em",
                        color: "red",
                        marginTop: "5px",
                      }}
                    >
                      {desktopsModalErrors.name}
                    </div>
                  )}
                </Form.Group>

                {/* Desktop Description */}
                <Form.Group
                  controlId="description"
                  style={{ marginBottom: "20px" }}
                >
                  <Form.Label
                    style={{ fontWeight: "bold", fontSize: "1.1rem" }}
                  >
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Add some personal notes"
                    name="description"
                    rows={4}
                    value={formDataDesktops.description ?? ""}
                    onChange={handleInputChangeDesktops}
                    style={{
                      resize: "vertical",
                      padding: "10px",
                      borderRadius: "6px",
                    }}
                  />
                </Form.Group>
              </Form>
            </Col>

            {/* RIGHT SECTION: Template Information */}
            <Col xs={12} md={7}>
              <div
                style={{
                  backgroundColor: selectedTemplate ? "#fff" : "transparent",
                  border: selectedTemplate
                    ? "1px solid #e2e8f0"
                    : "1px dashed #cbd5e1",
                  borderRadius: "12px",
                  padding: "30px",
                  display: "flex",
                  flexDirection: "column",
                  height: "610px",
                  boxShadow: selectedTemplate
                    ? "0 4px 12px rgba(0,0,0,0.03)"
                    : "none",
                }}
              >
                {!selectedTemplate ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "#64748b",
                      textAlign: "center",
                      fontSize: "1.1rem",
                    }}
                  >
                    <p>
                      Select a template to view its details and specifications
                      here.
                    </p>
                  </div>
                ) : (
                  <div
                    style={{ overflowY: "auto", paddingRight: "10px", flex: 1 }}
                  >
                    {/* Template Name and Info Button */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        borderBottom: "2px solid #f1f5f9",
                        paddingBottom: "15px",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          color: "#1e293b",
                          wordBreak: "break-word",
                          fontWeight: "600",
                        }}
                      >
                        {selectedTemplate.name}
                      </h4>
                      <button
                        onClick={() =>
                          navigate(`/templates/${selectedTemplate.id}`)
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "5px",
                        }}
                        title="View full template details"
                      >
                        <img
                          src={infoIcon}
                          alt="Info"
                          style={{ width: "24px", height: "24px" }}
                        />
                      </button>
                    </div>

                    {/* TEMPLATE DETAILS */}
                    {Object.keys(selectedTemplate).map((key) => {
                      const value = selectedTemplate[key];
                      if (
                        key === "id" ||
                        key === "name" ||
                        key === "operatingSystem"
                      )
                        return null;

                      // Flavor Info
                      if (
                        key === "flavor" ||
                        key === "flavorId" ||
                        key === "flavorName"
                      ) {
                        return (
                          <div
                            key={key}
                            style={{ marginTop: "20px", marginBottom: "20px" }}
                          >
                            <strong style={{ color: "#475569" }}>
                              {key.charAt(0).toUpperCase() + key.slice(1)}:
                            </strong>{" "}
                            <span
                              style={{ color: "#0f172a", fontWeight: "500" }}
                            >
                              {value || "-"}
                            </span>
                            {selectedFlavor && (
                              <div style={{ marginTop: "15px" }}>
                                <div
                                  style={{
                                    fontSize: "0.9em",
                                    color: "#64748b",
                                    fontStyle: "italic",
                                    marginBottom: "8px",
                                  }}
                                >
                                  More details about this flavor:
                                </div>
                                <div
                                  style={{
                                    padding: "15px",
                                    borderLeft: "4px solid #3b82f6",
                                    backgroundColor: "#f8fafc",
                                    borderRadius: "0 6px 6px 0",
                                    fontSize: "0.95em",
                                    color: "#334155",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {Object.entries(selectedFlavor)
                                    .filter(
                                      ([fk]) => fk !== "blob" && fk !== "name",
                                    )
                                    .map(([fk, fv]) => (
                                      <div
                                        key={fk}
                                        style={{ marginBottom: "8px" }}
                                      >
                                        <strong style={{ color: "#1e293b" }}>
                                          {fk}:
                                        </strong>
                                        &nbsp;
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: formatValue(fv).replace(
                                              /\n/g,
                                              "<br />",
                                            ),
                                          }}
                                        />
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // All other desktop fields (description, disk, os, etc.)
                      return (
                        <div
                          key={key}
                          style={{ marginBottom: "12px", fontSize: "1.05rem" }}
                        >
                          <strong style={{ color: "#475569" }}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}:
                          </strong>{" "}
                          <span
                            style={{
                              color: "#0f172a",
                              display: "inline-block",
                              wordBreak: "break-word",
                              fontWeight: "500",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: formatValue(value).replace(
                                /\n/g,
                                "<br />",
                              ),
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Cancel amd Confirm & Create Buttons */}
        <div
          style={{
            borderTop: "1px solid #e2e8f0",
            marginTop: "20px",
            paddingTop: "15px",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/")}
            style={{ padding: "8px 24px", fontWeight: "500", fontSize: "1rem" }}
          >
            Cancel
          </Button>

          {hasValidationError ? (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="create-desktop-tooltip">{tooltipMessage}</Tooltip>
              }
            >
              <span className="d-inline-block">
                <Button
                  variant="primary"
                  onClick={handleCreateDesktop}
                  disabled={true}
                  style={{
                    padding: "8px 24px",
                    fontWeight: "500",
                    fontSize: "1rem",
                    pointerEvents: "none",
                  }}
                >
                  Confirm & Create
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant="primary"
              onClick={handleCreateDesktop}
              disabled={isDisabled}
              style={{
                padding: "8px 24px",
                fontWeight: "500",
                fontSize: "1rem",
                pointerEvents: isDisabled ? "none" : "auto",
              }}
            >
              {isSubmitting ? "Creating..." : "Confirm & Create"}
            </Button>
          )}
        </div>
      </Container>

      {showModalCreateTemplates && (
        <ModalCreateTemplates
          showModalCreateTemplates={showModalCreateTemplates}
          handleCloseModalCreateTemplates={() =>
            setShowModalCreateTemplates(false)
          }
          handleCreateTemplate={handleCreateTemplate}
          formDataTemplates={formDataTemplates}
          handleInputChangeTemplates={handleInputChangeTemplates}
          loadingTemplates={loadingTemplates}
          templateModalErrors={templateModalErrors}
          flavorsList={flavorsList}
        />
      )}

      <Footer />
    </div>
  );
};

export default CreateDesktopPage;
